import type { Asset, Video, VideoManager, VideoReference } from '../../../../../generated/graphql-manager';

import {
  AlertIcon,
  CloseIcon,
  FileUploader,
  IconButton,
  ImageOutlineIcon,
  LinkStyleButton,
  Note,
  PencilIcon,
  PlayerIcon,
  Spinner,
  Tooltip,
  TrashIcon,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { VideoUrlModal } from 'src/manager/components/video-url-modal/video-url-modal';
import { useGetFeatures } from 'src/manager/hooks/use-get-features';
import { useGetVideo } from 'src/manager/hooks/use-get-video';

import {
  AssetUploadStatusCode,
  EventStage,
  GetVideoProblemCode,
  useCreateWebcastCoverImageMutation,
  useDeleteWebcastCoverImageMutation,
  useUpdateWebcastCoversMutation,
} from '../../../../../generated/graphql-manager';
import { uploadFile } from '../../../../../utils';
import { VideoBrowser } from '../../../../components/video-manager-browser/video-browser';
import { useVmproConnection } from '../../../../providers/vmpro-connector';
import { EventCover, EventCoverError } from '../event-cover';

import styles from './cover-media-uploader.module.css';

type Props = {
  eventStage: EventStage;
  language: string;
  testId: string;
  coverImage?: Asset | null;
  coverVideo?: VideoReference | null;
  videoVodManifestUrl?: string | null;
  disabled?: boolean;
  refetch: (options?: { poll: boolean }) => void;
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
};

export function CoverMediaUploader({
  eventStage,
  language,
  testId,
  coverImage,
  coverVideo,
  videoVodManifestUrl,
  disabled,
  refetch,
  onLoadStart,
  onLoadEnd,
}: Props) {
  const { t } = useTranslation();
  const { webcastId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const xhrRef = useRef<XMLHttpRequest>(undefined);
  const { isVmproLinked, saveState } = useVmproConnection();
  const { video, loading: videoLoading, videoError, onGetVideoData, onClearVideoError } = useGetVideo();

  const coverVideoRef = useRef(coverVideo);
  const onGetVideoDataRef = useRef(onGetVideoData);

  const { isVideoVodManifestUrlEnabled } = useGetFeatures();

  const [videoStructuredData, setVideoStructuredData] = useState<{ title: string; thumbnailUrl: string }>();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploaderError, setUploaderError] = useState('');
  const [serverError, setServerError] = useState('');
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false);
  const [videoSourceSelectionModalOpen, setVideoSourceSelectionModalOpen] = useState(false);

  const [createCoverImageMutation, { loading: creatingCoverImage }] = useCreateWebcastCoverImageMutation();
  const [deleteCoverImageMutation, { loading: deletingCoverImage }] = useDeleteWebcastCoverImageMutation();
  const [updateCoversMutation, { loading: updatingWebcast }] = useUpdateWebcastCoversMutation();

  const imageUploaded = coverImage?.status.code === AssetUploadStatusCode.UPLOADED;
  const imagePending = coverImage?.status.code === AssetUploadStatusCode.PENDING;
  const imageFailed = coverImage?.status.code === AssetUploadStatusCode.FAILED;
  const loading = imagePending || updatingWebcast || creatingCoverImage || deletingCoverImage;
  const videoCoversAvailable = eventStage !== EventStage.LIVE;
  const coverImageVisible = imageUploaded && coverImage?.downloadLink;

  const [fileUploaderAvailable, setFileUploaderAvailable] = useState(true);
  const hasError = serverError || imageFailed || videoError;
  const fileUploaderVisible =
    !hasError && fileUploaderAvailable && !coverImageVisible && !videoStructuredData && !videoVodManifestUrl;
  const hasContent = fileUploaderVisible || coverImageVisible || videoStructuredData || hasError || videoVodManifestUrl;
  const securityPolicyEnabled = video?.securityPolicy?.__typename === 'VideoManagerSecurityPolicy';

  useEffect(() => {
    if (creatingCoverImage || deletingCoverImage || updatingWebcast) onLoadStart?.();
    else onLoadEnd?.();
  }, [creatingCoverImage, deletingCoverImage, updatingWebcast, onLoadEnd, onLoadStart]);

  const stageNameForApi = {
    [EventStage.PRELIVE]: 'preLive',
    [EventStage.LIVE]: 'live',
    [EventStage.POSTLIVE]: 'postLive',
  }[eventStage];

  const toggleFileBrowserVisibility = () => {
    setFileBrowserOpen((state) => !state);
    if (!isVmproLinked) saveState(eventStage);
  };

  const toggleSourceSelectionModalVisibility = () => {
    setVideoSourceSelectionModalOpen((state) => !state);
  };

  const onVideoBrowserClose = () => {
    toggleFileBrowserVisibility();
    setVideoSourceSelectionModalOpen(false);
  };

  const onSaveVideoVodManifestUrl = (url: string) => {
    setVideoSourceSelectionModalOpen(false);

    updateCoversMutation({
      variables: {
        input: {
          id: webcastId,
          contents: [
            {
              language,
              [stageNameForApi]: {
                videoVodManifestUrl: { url },
                video: { reference: null },
              },
            },
          ],
        },
      },
      onCompleted: (data) => {
        if (data.updateWebcast.__typename !== 'UpdateWebcastSuccess') {
          return setServerError(t('manager.webcastSetup.media.errors.videoSaveFailed'));
        }
        refetch();
      },
    });
  };

  const cancelUpload = async () => {
    xhrRef.current?.abort();
    await deleteCoverImage();
    refetch();
  };

  const closeErrorMessage = async () => {
    await cancelUpload();
    setServerError('');
  };

  const createCoverImage = (files: File | File[]) => {
    const file = Array.isArray(files) ? files[0] : files;

    setUploaderError('');
    setUploadProgress(0);

    createCoverImageMutation({
      variables: { webcastId, eventStage, language },
      onCompleted: (data) => {
        if (data.createWebcastCoverImage.__typename !== 'AssetCreateResultSuccess') {
          return setServerError(t('manager.webcastSetup.media.errors.imageUploadFailed'));
        }

        if (coverVideo) deleteCoverVideo();

        const {
          uploadLink: { url, token },
        } = data.createWebcastCoverImage;

        xhrRef.current = uploadFile({
          file,
          token,
          url,
          onProgress: setUploadProgress,
          onSuccess: () => {
            setFileUploaderAvailable(false);
            refetch({ poll: true });
          },
          onError: () => setUploaderError(t('manager.webcastSetup.media.errors.imageUploadFailed')),
        });
      },
    });
  };

  const deleteCoverImage = () =>
    deleteCoverImageMutation({
      variables: { webcastId, eventStage, language },
      onCompleted: () => {
        refetch();
        setFileUploaderAvailable(true);
      },
    });

  const createCoverVideo = async ({ id: videoId, channelId }: Video, videoManagerId?: VideoManager['id']) => {
    setFileUploaderAvailable(false);
    toggleFileBrowserVisibility();

    if (coverImage) await deleteCoverImage();

    updateCoversMutation({
      variables: {
        input: {
          id: webcastId,
          contents: [
            {
              language,
              [stageNameForApi]: {
                video: { reference: { videoId, channelId, videoManagerId } },
                videoVodManifestUrl: { url: null },
              },
            },
          ],
        },
      },
      onCompleted: (data) => {
        if (data.updateWebcast.__typename !== 'UpdateWebcastSuccess') {
          return setServerError(t('manager.webcastSetup.media.errors.videoSaveFailed'));
        }
        refetch();
      },
    });
  };

  const deleteCoverVideo = () => {
    updateCoversMutation({
      variables: {
        input: {
          id: webcastId,
          contents: [{ language, [stageNameForApi]: { video: { reference: null } } }],
        },
      },
      onCompleted: (data) => {
        if (data.updateWebcast.__typename !== 'UpdateWebcastSuccess') {
          return setServerError(t('manager.webcastSetup.media.errors.videoDeleteFailed'));
        }
        onClearVideoError();
        refetch();
        setFileUploaderAvailable(true);
      },
    });
  };

  const deleteVideoVodManifestUrl = () => {
    updateCoversMutation({
      variables: {
        input: {
          id: webcastId,
          contents: [
            {
              language,
              [stageNameForApi]: {
                videoVodManifestUrl: { url: null },
              },
            },
          ],
        },
      },
      onCompleted: (data) => {
        if (data.updateWebcast.__typename !== 'UpdateWebcastSuccess') {
          return setServerError(t('manager.webcastSetup.media.errors.videoDeleteFailed'));
        }
        refetch();
      },
    });
  };

  useEffect(() => {
    if (coverVideo?.videoManagerId && coverVideo.videoId) {
      onGetVideoData(coverVideo.videoManagerId, coverVideo.videoId);
    } else {
      setVideoStructuredData(undefined);
    }
  }, [coverVideo, onGetVideoData]);

  useEffect(() => {
    if (!video) return;

    setVideoStructuredData({
      title: video.title,
      thumbnailUrl: video.thumbnail || '',
    });
  }, [video]);

  useEffect(() => {
    const previousState = searchParams.get('state');

    if (!previousState) return;

    if (previousState === eventStage && isVmproLinked) {
      if (coverVideoRef.current?.videoId && coverVideoRef.current.videoManagerId) {
        onGetVideoDataRef.current(coverVideoRef.current.videoManagerId, coverVideoRef.current.videoId);
      } else {
        setFileBrowserOpen(true);
      }
    }
  }, [eventStage, searchParams, isVmproLinked]);

  return (
    <>
      <div className={styles.wrapper}>
        {(loading || videoLoading || !hasContent) && <Spinner size={40} className={styles.spinner} />}

        {fileUploaderVisible && (
          <>
            <FileUploader
              info={
                <Trans
                  i18nKey="manager.webcastSetup.media.languageContent.fileUploader.uploaderHeading"
                  components={{ u: <u /> }}
                />
              }
              icon={null}
              acceptedFormats=".jpg, .jpeg, .png, .svg, .gif"
              maximumFileSize={3145728} // 3MB
              disabled={creatingCoverImage || disabled}
              progress={uploadProgress}
              errorMessage={uploaderError}
              testIdPrefix={`${testId}-cover-`}
              onChange={createCoverImage}
              onClose={cancelUpload}
              additionalUploadInformation={[
                t('manager.webcastSetup.media.coverImageUploder.restrictions'),
                t('manager.webcastSetup.media.coverImageUploder.recommendations'),
              ]}
            />

            {videoCoversAvailable && !hasError && (
              <LinkStyleButton
                className={styles.linkStyleButton}
                data-testid={`${testId}-video-manager-button`}
                disabled={loading || disabled}
                onClick={
                  isVideoVodManifestUrlEnabled ? toggleSourceSelectionModalVisibility : toggleFileBrowserVisibility
                }
              >
                {`${t('manager.webcastSetup.media.videoBrowserButton')}${!isVideoVodManifestUrlEnabled ? ` ${t('manager.webcastSetup.media.vmPro')}` : ''}`}
              </LinkStyleButton>
            )}
          </>
        )}

        {coverImageVisible && (
          <EventCover
            imageUrl={coverImage.downloadLink || ''}
            icon={<ImageOutlineIcon />}
            label={t('manager.webcastSetup.media.coverImage')}
            disabled={deletingCoverImage || disabled}
            testIdPrefix={`${testId}-`}
            onDelete={deleteCoverImage}
          />
        )}

        {videoStructuredData && (
          <EventCover
            imageUrl={videoStructuredData.thumbnailUrl}
            icon={<PlayerIcon />}
            label={videoStructuredData.title}
            disabled={updatingWebcast || disabled}
            testIdPrefix={`${testId}-`}
            restricted={securityPolicyEnabled}
            onDelete={deleteCoverVideo}
          />
        )}

        {videoVodManifestUrl && (
          <div data-testid={`${testId}-custom-hls-url`} className={styles.videoVodManifestUrl}>
            <span className={styles.value}>{videoVodManifestUrl}</span>

            <div className={styles.buttons}>
              <Tooltip label={t('manager.webcastSetup.media.videoVodManifestUrl.edit')}>
                <IconButton
                  data-testid={`${testId}-edit-custom-hls-url-button`}
                  className={styles.iconButton}
                  rounded
                  aria-label={t('manager.webcastSetup.media.videoVodManifestUrl.edit')}
                  onClick={() => setVideoSourceSelectionModalOpen(true)}
                >
                  <PencilIcon />
                </IconButton>
              </Tooltip>

              <Tooltip label={t('manager.webcastSetup.media.videoVodManifestUrl.remove')}>
                <IconButton
                  data-testid={`${testId}-remove-custom-hls-url-button`}
                  className={styles.iconButton}
                  rounded
                  aria-label={t('manager.webcastSetup.media.videoVodManifestUrl.remove')}
                  onClick={deleteVideoVodManifestUrl}
                >
                  <TrashIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        )}

        {hasError && (videoError === 'UNAUTHORIZED_ACCESS' || videoError === 'VMPRO_LINK_MISSING') && (
          <EventCoverError
            coverVideoId={coverVideo?.videoId}
            videoError={videoError}
            disabled={updatingWebcast || disabled}
            testIdPrefix={`${testId}-`}
            onDelete={deleteCoverVideo}
            onLinkToVmpro={toggleFileBrowserVisibility}
          />
        )}

        {videoError === GetVideoProblemCode.VIDEO_NOT_FOUND && (
          <Note
            variant="negative"
            className={styles.deleteVideoNote}
            data-testid={`${testId}-cover-video-deleted-message`}
          >
            {t('manager.webcastSetup.media.errors.videoDeleted')}
            <LinkStyleButton
              className={styles.deleteVideoButton}
              disabled={updatingWebcast || disabled}
              onClick={deleteCoverVideo}
            >
              {t('manager.webcastSetup.media.deleteVideo')}
            </LinkStyleButton>
          </Note>
        )}

        {hasError &&
          videoError !== GetVideoProblemCode.UNAUTHORIZED_ACCESS &&
          videoError !== GetVideoProblemCode.VMPRO_LINK_MISSING &&
          videoError !== GetVideoProblemCode.VIDEO_NOT_FOUND && (
            <span className={styles.errorMessage}>
              <AlertIcon />

              <span data-testid={`${testId}-cover-error-message`}>
                {serverError || t('manager.webcastSetup.media.errors.imageUploadFailed')}
              </span>

              <Tooltip label={t('manager.webcastSetup.media.closeErrorButtonLabel')}>
                <IconButton
                  data-testid={`${testId}-cover-error-close-button`}
                  rounded
                  onClick={closeErrorMessage}
                  aria-label={t('manager.webcastSetup.media.closeErrorButtonLabel')}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </span>
          )}
      </div>

      <VideoBrowser
        isOpen={fileBrowserOpen}
        title={t('manager.webcastSetup.media.videoBrowserTitle')}
        videoDetailsTitle={t('manager.webcastSetup.media.videoDetailsModalTitle')}
        onChooseVideo={createCoverVideo}
        onClose={onVideoBrowserClose}
      />

      <VideoUrlModal
        isOpen={videoSourceSelectionModalOpen}
        initialVideoVodManifestUrl={videoVodManifestUrl}
        onSelectVideoFromVmpro={toggleFileBrowserVisibility}
        onSaveVideoVodManifestUrl={onSaveVideoVodManifestUrl}
        onClose={() => setVideoSourceSelectionModalOpen(false)}
      />
    </>
  );
}
